import { FC, Fragment, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useDictionaryReviewQueue, useDictionaryReviewSummary } from '../../hooks/useDictionaryReview';
import type {
    DictionaryReviewItem,
    DictionaryReviewStatus,
    RefAnchorEvidence,
} from '../../types/dictionaryReview';

// 학습 언어(headLang) — whitelist 3종
const HEAD_LANG_OPTIONS = [
    { value: 'en', label: '영어' },
    { value: 'ja', label: '일본어' },
    { value: 'ko', label: '한국어' },
];

// 뜻 언어(meaningLang)
const MEANING_LANG_OPTIONS = [
    { value: 'ko', label: '한국어' },
    { value: 'en', label: '영어' },
    { value: 'ja', label: '일본어' },
];

const STATUS_OPTIONS: { value: DictionaryReviewStatus; label: string }[] = [
    { value: 'ALL', label: '전체' },
    { value: 'POS_REASSIGN', label: 'POS 오배정' },
    { value: 'QUARANTINED', label: '격리' },
];

const STATUS_META: Record<string, { label: string; color: 'warning' | 'error' | 'default' }> = {
    POS_REASSIGN: { label: 'POS 오배정', color: 'warning' },
    MISMATCH_QUARANTINED: { label: '격리', color: 'error' },
};

const AnchorRow: FC<{ anchor: RefAnchorEvidence }> = ({ anchor }) => {
    const outScopeMatch = anchor.overlapsTranslation && !anchor.inScope;
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                py: 0.5,
                px: 1,
                borderRadius: 1,
                bgcolor: outScopeMatch ? 'warning.50' : 'transparent',
            }}
        >
            <Chip size="small" variant="outlined" label={anchor.source} sx={{ minWidth: 92 }} />
            <Chip
                size="small"
                label={anchor.pos ?? 'POS 없음'}
                color={anchor.inScope ? 'primary' : 'default'}
                variant={anchor.inScope ? 'filled' : 'outlined'}
            />
            {anchor.overlapsTranslation ? (
                <Chip
                    size="small"
                    color={outScopeMatch ? 'warning' : 'success'}
                    label={outScopeMatch ? '번역과 겹침(다른 품사!)' : '번역과 겹침'}
                />
            ) : (
                <Chip size="small" variant="outlined" label="겹침 없음" />
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1, wordBreak: 'break-word' }}>
                {(anchor.equivalents ?? []).join(', ') || '—'}
            </Typography>
        </Box>
    );
};

const ReviewTableRow: FC<{ item: DictionaryReviewItem }> = ({ item }) => {
    const [open, setOpen] = useState(false);
    const meta = STATUS_META[item.validationStatus];
    return (
        <Fragment>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell padding="checkbox">
                    <IconButton size="small" onClick={() => setOpen((o) => !o)}>
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{item.word}</TableCell>
                <TableCell>
                    <Chip size="small" variant="outlined" label={item.declaredPos ?? '—'} />
                </TableCell>
                <TableCell sx={{ maxWidth: 240, wordBreak: 'break-word' }}>{item.translation ?? '—'}</TableCell>
                <TableCell>
                    {(item.anchorPos ?? []).length > 0 ? (
                        <Stack direction="row" spacing={0.5}>
                            {(item.anchorPos ?? []).map((p) => (
                                <Chip key={p} size="small" color="warning" label={p} />
                            ))}
                        </Stack>
                    ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                </TableCell>
                <TableCell>
                    {meta ? (
                        <Chip size="small" color={meta.color} label={meta.label} />
                    ) : (
                        item.validationStatus
                    )}
                </TableCell>
                <TableCell align="right">{item.validationAttemptCount}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                    {item.validationCheckedAt ? item.validationCheckedAt.replace('T', ' ').slice(0, 16) : '—'}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={8} sx={{ py: 0, border: open ? undefined : 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1.5, px: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                참조 앵커 evidence
                                <Tooltip title="참조 사전 등가어는 모니터링 표시 전용입니다(CC BY-SA, dictionary_word에 복사하지 않음).">
                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                                        ⓘ 표시 전용
                                    </Typography>
                                </Tooltip>
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                dictId {item.dictId} · ref {item.validationRefVersion ?? '—'} · provider {item.aiProvider ?? '—'}
                                {item.validationReason ? ` · reason ${item.validationReason}` : ''}
                            </Typography>
                            {(item.anchors ?? []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    참조 앵커 없음
                                </Typography>
                            ) : (
                                <Stack spacing={0.25}>
                                    {(item.anchors ?? []).map((a, i) => (
                                        <AnchorRow key={`${a.source}-${a.pos}-${i}`} anchor={a} />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </Fragment>
    );
};

const SummaryCard: FC<{ title: string; count: number; color: string }> = ({ title, count, color }) => (
    <Card>
        <CardContent>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
            <Typography variant="h4" sx={{ color, fontWeight: 700 }}>{count.toLocaleString()}</Typography>
        </CardContent>
    </Card>
);

const DictionaryReviewList: FC = () => {
    const [status, setStatus] = useState<DictionaryReviewStatus>('ALL');
    const [headLang, setHeadLang] = useState('en');
    const [meaningLang, setMeaningLang] = useState('ko');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);

    const summaryQ = useDictionaryReviewSummary({ headLang, meaningLang });
    const queueQ = useDictionaryReviewQueue({ status, headLang, meaningLang, page, size });

    const resetPage = () => setPage(0);

    return (
        <Box sx={{ p: 3, maxWidth: 1200 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>사전 검증 리뷰</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                참조 사전(POS 앵커) 대조에서 의심으로 분류된 행을 모니터링합니다. <strong>읽기 전용</strong> —
                교정은 별도 경로로 수행합니다. POS 오배정(번역이 선언 품사와 다른 품사의 뜻) · 격리(앵커와 0겹침)만 노출됩니다.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <SummaryCard title="POS 오배정" count={summaryQ.data?.posReassignCount ?? 0} color="#ed6c02" />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <SummaryCard title="격리" count={summaryQ.data?.quarantinedCount ?? 0} color="#d32f2f" />
                </Grid>
            </Grid>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            select size="small" label="상태" value={status} sx={{ width: 140 }}
                            onChange={(e) => { setStatus(e.target.value as DictionaryReviewStatus); resetPage(); }}
                        >
                            {STATUS_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select size="small" label="학습 언어" value={headLang} sx={{ width: 140 }}
                            onChange={(e) => { setHeadLang(e.target.value); resetPage(); }}
                        >
                            {HEAD_LANG_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select size="small" label="뜻 언어" value={meaningLang} sx={{ width: 140 }}
                            onChange={(e) => { setMeaningLang(e.target.value); resetPage(); }}
                        >
                            {MEANING_LANG_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </CardContent>
            </Card>

            {queueQ.isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {queueQ.error?.message ?? '리뷰 큐 조회에 실패했습니다.'}
                </Alert>
            )}

            <Card>
                {queueQ.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>단어</TableCell>
                                        <TableCell>선언 POS</TableCell>
                                        <TableCell>번역(우리 캐시)</TableCell>
                                        <TableCell>앵커 POS</TableCell>
                                        <TableCell>상태</TableCell>
                                        <TableCell align="right">시도</TableCell>
                                        <TableCell>검증 시각</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(queueQ.data?.content ?? []).length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                                                해당 조건의 리뷰 대상 행이 없습니다.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        (queueQ.data?.content ?? []).map((item) => (
                                            <ReviewTableRow key={item.dictId} item={item} />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={queueQ.data?.totalCount ?? 0}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={size}
                            onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); resetPage(); }}
                            rowsPerPageOptions={[20, 50, 100]}
                            labelRowsPerPage="페이지당"
                        />
                    </>
                )}
            </Card>
        </Box>
    );
};

export default DictionaryReviewList;
